import {
  ApplicationConfig,
  importProvidersFrom,
  provideZoneChangeDetection,
} from '@angular/core';
import { isStaleChunkError, recoverFromStaleChunk } from './core/stale-chunk';
import { NavigationError, provideRouter, withNavigationErrorHandler } from '@angular/router';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeuix/themes/aura';
import { definePreset } from '@primeuix/themes';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FiSpokePreset = definePreset(Aura, ({
  primitive: {
    borderRadius: {
      none: '0',
      xs: '2px',
      sm: '3px',
      md: '6px',
      lg: '8px',
      xl: '12px',
    },
  },
  semantic: {
    fontFamily: '"Nunito", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    primary: {
      50:  '#e6eaf4',
      100: '#c0cbe3',
      200: '#97abd1',
      300: '#6d8bbf',
      400: '#4d72b1',
      500: '#01205f',
      600: '#011c55',
      700: '#011749',
      800: '#01123d',
      900: '#000a28',
      950: '#000515',
    },
    colorScheme: {
      light: {
        primary: {
          color: '#01205f',
          contrastColor: '#ffffff',
          hoverColor: '#0039ac',
          activeColor: '#011749',
        },
        highlight: {
          background: '#01205f',
          focusBackground: '#011749',
          color: '#ffffff',
          focusColor: '#ffffff',
        },
        surface: {
          0:   '#ffffff',
          50:  '#f8f9fa',
          100: '#e9ecef',
          200: '#dee2e6',
          300: '#ced4da',
          400: '#b2bec3',
          500: '#adb5bd',
          600: '#6c757d',
          700: '#495057',
          800: '#343a40',
          900: '#212529',
          950: '#1a1d20',
        },
        text: {
          color:           '#495057',
          hoverColor:      '#212529',
          mutedColor:      '#6c757d',
          hoverMutedColor: '#495057',
        },
        content: {
          background:      '#ffffff',
          hoverBackground: '#e9ecef',
          borderColor:     '#dee2e6',
          color:           '#495057',
          hoverColor:      '#495057',
        },
        overlay: {
          select: {
            background:  '#ffffff',
            borderColor: 'transparent',
            color:       '#495057',
            shadow:      '0 3px 6px 0 rgba(0,0,0,0.1)',
          },
          popover: {
            background:  '#ffffff',
            borderColor: 'transparent',
            color:       '#495057',
            shadow:      '0 1px 6px 0 rgba(0,0,0,0.1)',
            padding:     '1rem',
          },
          modal: {
            background:   '#ffffff',
            borderColor:  'transparent',
            color:        '#495057',
            shadow:       '0 0 14px 0 rgba(0,0,0,0.1)',
            borderRadius: '10px',
          },
          navigation: {
            shadow: '0 1px 6px 0 rgba(0,0,0,0.1)',
          },
        },
        formField: {
          background:             '#ffffff',
          disabledBackground:     '#e9ecef',
          filledBackground:       '#f8f9fa',
          filledHoverBackground:  '#f8f9fa',
          filledFocusBackground:  '#f8f9fa',
          borderColor:            '#ced4da',
          hoverBorderColor:       '#01205f',
          focusBorderColor:       '#01205f',
          invalidBorderColor:     '#e4677e',
          color:                  '#495057',
          disabledColor:          '#6c757d',
          placeholderColor:       '#6c757d',
          floatLabelColor:        '#6c757d',
          floatLabelFocusColor:   '#01205f',
          floatLabelActiveColor:  '#6c757d',
          iconColor:              '#495057',
          shadow:                 'none',
          paddingX:               '0.5rem',
          paddingY:               '0.5rem',
          sm: { fontSize: '0.875rem', paddingX: '0.4375rem', paddingY: '0.4375rem' },
          lg: { fontSize: '1.125rem', paddingX: '0.625rem',  paddingY: '0.625rem'  },
          borderRadius:           '3px',
          transitionDuration:     '0.2s',
        },
        list: {
          option: {
            focusBackground:        '#f8f9fa',
            selectedBackground:     '#01205f',
            selectedFocusBackground:'#011749',
            color:                  '#495057',
            focusColor:             '#495057',
            selectedColor:          '#ffffff',
            selectedFocusColor:     '#ffffff',
            padding:                '.5rem 1rem',
            borderRadius:           '0',
          },
          optionGroup: {
            background:  'transparent',
            color:       '#6c757d',
            fontWeight:  '600',
            padding:     '.75rem 1rem',
          },
        },
        navigation: {
          item: {
            focusBackground:  '#f8f9fa',
            activeBackground: '#e9ecef',
            color:            '#495057',
            focusColor:       '#495057',
            activeColor:      '#495057',
            padding:          '.75rem 1rem',
            borderRadius:     '0',
          },
          submenuLabel: {
            background:  'transparent',
            color:       '#495057',
            fontWeight:  '600',
          },
          submenuIcon: {
            color:       '#6c757d',
            focusColor:  '#495057',
            activeColor: '#6c757d',
          },
        },
      },
    },
  },
  components: {
    button: {
      root: {
        borderRadius: '5px',
        paddingX:     '1rem',
        paddingY:     '0.5rem',
        sm: { fontSize: '0.875rem', paddingX: '0.75rem',  paddingY: '0.375rem' },
        lg: { fontSize: '1.125rem', paddingX: '1.25rem',  paddingY: '0.625rem' },
      },
    },
    inputtext: {
      root: {
        borderRadius:      '3px',
        paddingX:          '0.5rem',
        paddingY:          '0.5rem',
        shadow:            '0 0 0 0.2rem #bfd1f6',
        transitionDuration:'0.2s',
      },
    },
    select: {
      root: {
        borderColor:  '#dee2f3',
        borderRadius: '3px',
        paddingX:     '0.5rem',
        paddingY:     '0.5rem',
        shadow:       '0 0 0 0.2rem #bfd1f6',
      },
      dropdown: {
        width: '2.5rem',
        color: '#6c757d',
      },
      option: {
        focusBackground:         '#e9ecef',
        selectedBackground:      '#01205f',
        selectedFocusBackground: '#011749',
        color:                   '#495057',
        focusColor:              '#495057',
        selectedColor:           '#ffffff',
        selectedFocusColor:      '#ffffff',
        padding:                 '.5rem 1rem',
        borderRadius:            '0',
      },
      overlay: {
        background:   '#ffffff',
        borderColor:  'transparent',
        borderRadius: '6px',
        shadow:       '0 3px 6px 0 rgba(0,0,0,0.1)',
      },
      header: {
        background:   '#f8f9fa',
        borderColor:  'transparent',
        padding:      '.5rem 1rem',
        color:        '#495057',
      },
    },
    autocomplete: {
      root: { borderRadius: '3px', shadow: '0 0 0 0.2rem #bfd1f6' },
      overlay: { background: '#ffffff', borderColor: 'transparent', borderRadius: '6px', shadow: '0 3px 6px 0 rgba(0,0,0,0.1)' },
      option: { focusBackground: '#e9ecef', selectedBackground: '#01205f', selectedFocusBackground: '#011749', color: '#495057', selectedColor: '#ffffff', padding: '.5rem 1rem', borderRadius: '0' },
      header: { background: '#f8f9fa', borderColor: 'transparent', padding: '.5rem 1rem' },
    },
    multiselect: {
      root: { borderRadius: '3px', shadow: '0 0 0 0.2rem #bfd1f6' },
      overlay: { background: '#ffffff', borderColor: 'transparent', borderRadius: '6px', shadow: '0 3px 6px 0 rgba(0,0,0,0.1)' },
      option: { focusBackground: '#e9ecef', selectedBackground: '#01205f', selectedFocusBackground: '#011749', color: '#495057', selectedColor: '#ffffff', padding: '.5rem 1rem', borderRadius: '0' },
      header: { background: '#f8f9fa', borderColor: 'transparent', padding: '.5rem 1rem' },
    },
    listbox: {
      root: { borderRadius: '3px', shadow: '0 0 0 0.2rem #bfd1f6' },
      option: { focusBackground: '#e9ecef', selectedBackground: '#01205f', selectedFocusBackground: '#011749', color: '#495057', selectedColor: '#ffffff', padding: '.5rem 1rem', borderRadius: '0' },
      header: { background: '#f8f9fa', borderColor: 'transparent', padding: '.5rem 1rem' },
    },
    checkbox: {
      root: {
        borderRadius:      '2px',
        width:             '20px',
        height:            '20px',
        shadow:            'none',
        focusShadow:       'none',
        transitionDuration:'0.2s',
      },
      colorScheme: {
        light: {
          root: {
            background:             '#ffffff',
            checkedBackground:      '#01205f',
            checkedHoverBackground: '#011749',
            disabledBackground:     '#e9ecef',
            borderColor:            '#ced4da',
            hoverBorderColor:       '#01205f',
            focusBorderColor:       '#ced4da',
            checkedBorderColor:     '#01205f',
            checkedHoverBorderColor:'#011749',
            checkedFocusBorderColor:'#01205f',
            invalidBorderColor:     '#e4677e',
          },
          icon: {
            color:             '#ffffff',
            checkedColor:      '#ffffff',
            checkedHoverColor: '#ffffff',
            size:              '14px',
          },
        },
      },
    },
    radiobutton: {
      root: {
        width:             '20px',
        height:            '20px',
        shadow:            'none',
        focusShadow:       'none',
        transitionDuration:'0.2s',
      },
      colorScheme: {
        light: {
          root: {
            background:             '#ffffff',
            checkedBackground:      '#01205f',
            checkedHoverBackground: '#011749',
            disabledBackground:     '#e9ecef',
            borderColor:            '#ced4da',
            hoverBorderColor:       '#01205f',
            checkedBorderColor:     '#01205f',
            checkedHoverBorderColor:'#011749',
          },
          icon: {
            color:             '#ffffff',
            checkedColor:      '#ffffff',
            checkedHoverColor: '#ffffff',
            size:              '12px',
          },
        },
      },
    },
    toggleswitch: {
      root: {
        width:             '1.8rem',
        height:            '1.1rem',
        borderRadius:      '30px',
        shadow:            'none',
        focusShadow:       'none',
        transitionDuration:'0.2s',
      },
      handle: {
        size: '0.8rem',
      },
      colorScheme: {
        light: {
          root: {
            background:           '#cbd5e1',
            hoverBackground:      '#c3cad2',
            checkedBackground:    '#83ce9f',
            checkedHoverBackground:'#83ce9f',
            disabledBackground:   '#e9ecef',
          },
          handle: {
            background:        '#ffffff',
            checkedBackground: '#ffffff',
            disabledBackground:'#ffffff',
          },
        },
      },
    },
    dialog: {
      root: {
        borderRadius: '10px',
        shadow:       '0 0 14px 0 rgba(0,0,0,0.1)',
        background:   '#ffffff',
        color:        '#495057',
      },
      header: {
        padding:     '10px',
        background:  '#ffffff',
        borderColor: '#dee2e6',
        borderWidth: '0 0 1px 0',
        color:       '#01205f',
        fontWeight:  '700',
        fontSize:    '1rem',
      },
      content: {
        padding:    '15px',
        background: '#ffffff',
        color:      '#495057',
      },
      footer: {
        padding:     '10px',
        background:  '#ffffff',
        borderColor: '#dee2e6',
        borderWidth: '1px 0 0 0',
        gap:         '0.5rem',
      },
    },
    tooltip: {
      colorScheme: {
        light: {
          root: {
            background: '#01205f',
            color:      '#ffffff',
            shadow:     '0 0 14px 0 rgba(0,0,0,0.1)',
          },
          arrow: {
            background: '#01205f',
          },
        },
      },
    },
    tag: {
      root: {
        paddingX:    '15px',
        paddingY:    '2px',
        borderRadius:'4px',
        fontSize:    '0.75rem',
        fontWeight:  '700',
      },
    },
    badge: {
      root: {
        borderRadius: '4px',
        fontSize:     '0.75rem',
        fontWeight:   '700',
        minWidth:     '1.5rem',
        height:       '1.5rem',
      },
    },
    accordion: {
      header: {
        background:      '#ffffff',
        hoverBackground: '#f8f9fa',
        activeBackground:'#ffffff',
        color:           '#495057',
        hoverColor:      '#495057',
        activeColor:     '#01205f',
        padding:         '10px',
        fontWeight:      '600',
        borderColor:     '#e5e5e5',
        borderWidth:     '0 0 1px 0',
      },
      content: {
        background:  '#ffffff',
        borderColor: '#e5e5e5',
        borderWidth: '0 0 1px 0',
        color:       '#495057',
        padding:     '10px',
      },
    },
    tabs: {
      tablist: {
        background:  'transparent',
        borderColor: '#dee2e6',
        borderWidth: '0 0 2px 0',
      },
      tab: {
        background:         '#f8f9fa',
        hoverBackground:    '#ffffff',
        activeBackground:   '#ffffff',
        borderColor:        '#dee2e6',
        hoverBorderColor:   '#9baace',
        activeBorderColor:  '#01205f',
        color:              '#95a5c8',
        hoverColor:         '#495057',
        activeColor:        '#01205f',
        padding:            '10px',
        fontWeight:         '400',
        borderRadius:       '7px 7px 0 0',
        focusShadow:        'none',
      },
      tabpanel: {
        background: '#ffffff',
        color:      '#495057',
        padding:    '10px 0',
      },
    },
    datatable: {
      headerCell: {
        background:       '#f8f9fa',
        hoverBackground:  '#e9ecef',
        selectedBackground:'#f8f9fa',
        borderColor:      '#e9ecef',
        borderWidth:      '0 0 1px 0',
        color:            '#495057',
        hoverColor:       '#495057',
        selectedColor:    '#01205f',
        padding:          '1rem 1rem',
        fontWeight:       '600',
      },
      bodyCell: {
        borderColor: 'rgba(0,0,0,0.08)',
        borderWidth: '0 0 1px 0',
        padding:     '1rem 1rem',
      },
      footerCell: {
        background:  '#f8f9fa',
        borderColor: '#e9ecef',
        borderWidth: '0 0 1px 0',
        color:       '#495057',
        padding:     '1rem 1rem',
        fontWeight:  '600',
      },
      paginator: {
        topBorderWidth:    '1px 0 1px 0',
        bottomBorderWidth: '0 0 1px 0',
      },
    },
    paginator: {
      root: {
        background:   '#ffffff',
        borderColor:  '#e9ecef',
        padding:      '0.5rem 1rem',
        borderRadius: '0',
        color:        '#6c757d',
      },
      navButton: {
        background:       'transparent',
        hoverBackground:  '#e9ecef',
        selectedBackground:'#01205f',
        borderColor:      'transparent',
        color:            '#6c757d',
        hoverColor:       '#6c757d',
        selectedColor:    '#ffffff',
        width:            '2.357rem',
        height:           '2.357rem',
        borderRadius:     '3px',
        focusShadow:      '0 0 0 0.2rem #bfd1f6',
      },
    },
    message: {
      root: {
        borderWidth:    '0 0 0 4px',
        borderRadius:   '6px',
        padding:        '1rem 1.5rem',
        margin:         '1rem 0',
      },
      colorScheme: {
        light: {
          infoBackground:    '#039BE5',
          successBackground: '#43A047',
          warnBackground:    '#FFB300',
          errorBackground:   '#E53935',
          infoBorderColor:   '#027cb7',
          infoColor:         '#ffffff',
          successColor:      '#ffffff',
          warnColor:         '#495057',
          errorColor:        '#ffffff',
        },
      },
    },
    toast: {
      root: {
        borderWidth:  '0 0 0 4px',
        borderRadius: '6px',
        padding:      '1rem',
        shadow:       '0 3px 14px 0 rgba(0,0,0,0.3)',
      },
      colorScheme: {
        light: {
          infoBackground:    '#039BE5',
          successBackground: '#43A047',
          warnBackground:    '#FFB300',
          errorBackground:   '#E53935',
        },
      },
    },
    menu: {
      root: {
        background:   '#ffffff',
        borderColor:  '#dee2e6',
        borderRadius: '6px',
        color:        '#495057',
        width:        '12.5rem',
        shadow:       '0 1px 6px 0 rgba(0,0,0,0.1)',
        padding:      '.25rem 0',
      },
      item: {
        focusBackground:  '#f8f9fa',
        activeBackground: '#e9ecef',
        color:            '#495057',
        focusColor:       '#495057',
        activeColor:      '#495057',
        disabledColor:    '#6c757d',
        padding:          '.75rem 1rem',
        borderRadius:     '0',
        gap:              '0.5rem',
        iconColor:        '#6c757d',
        iconFocusColor:   '#6c757d',
        iconActiveColor:  '#6c757d',
      },
      separator: {
        borderColor: '#dee2e6',
        margin:      '.25rem 0',
      },
      submenuLabel: {
        background:  '#ffffff',
        color:       '#495057',
        fontWeight:  '600',
        padding:     '.75rem 1rem',
      },
    },
    panel: {
      root: {
        background:   '#ffffff',
        borderColor:  '#dee2e6',
        borderWidth:  '1px',
        color:        '#495057',
        borderRadius: '6px',
      },
      header: {
        background:   '#f8f9fa',
        color:        '#495057',
        borderColor:  '#dee2e6',
        borderWidth:  '1px',
        padding:      '1rem',
        fontWeight:   '600',
        borderRadius: '6px 6px 0 0',
      },
      content: {
        background:  '#ffffff',
        borderColor: '#dee2e6',
        borderWidth: '0 1px 1px 1px',
        color:       '#495057',
        padding:     '1rem',
      },
      footer: {
        background:  '#ffffff',
        borderColor: '#dee2e6',
        borderWidth: '0 1px 1px 1px',
        color:       '#495057',
        padding:     '0.5rem 1rem',
      },
    },
    card: {
      root: {
        background:   '#ffffff',
        borderRadius: '6px',
        color:        '#495057',
        shadow:       '0 2px 1px -1px rgba(0,0,0,.2),0 1px 1px 0 rgba(0,0,0,.14),0 1px 3px 0 rgba(0,0,0,.12)',
      },
      body:    { padding: '1rem',  gap: '0.5rem' },
      caption: { gap: '0.5rem' },
      title:   { fontSize: '1.5rem', fontWeight: '700' },
      subtitle:{ color: '#6c757d' },
    },
    overlaypanel: {
      root: {
        background:   '#ffffff',
        borderColor:  'transparent',
        color:        '#495057',
        borderRadius: '6px',
        shadow:       '0 1px 6px 0 rgba(0,0,0,0.1)',
        padding:      '1rem',
      },
    },
    divider: {
      root: { borderColor: '#dee2e6', borderWidth: '1px' },
    },
    chip: {
      root: {
        background:   '#dee2e6',
        borderRadius: '16px',
        paddingX:     '0.5rem',
        paddingY:     '0.25rem',
        color:        '#495057',
        gap:          '0.5rem',
      },
    },
    progressbar: {
      root: {
        height:       '1.5rem',
        borderRadius: '3px',
        background:   '#dee2e6',
        border:       '0 none',
      },
      value: { background: '#01205f' },
      label: { color: '#ffffff', fontSize: '0.75rem', fontWeight: '700' },
    },
    skeleton: {
      root: {
        background:          '#e9ecef',
        animationBackground: 'rgba(255,255,255,0.4)',
        borderRadius:        '6px',
      },
    },
    slider: {
      root: {
        background:   '#dee2e6',
        border:       '0 none',
        borderRadius: '6px',
      },
      range: { background: '#01205f' },
      handle: {
        width:         '1.143rem',
        height:        '1.143rem',
        background:    '#ffffff',
        borderRadius:  '50%',
        borderColor:   '#01205f',
        borderWidth:   '2px',
        hoverBorderColor: '#01205f',
        focusShadow:   '0 0 0 0.2rem #bfd1f6',
      },
    },
    stepper: {
      root: { transitionDuration: '0.2s' },
      separator: {
        background:       '#dee2e6',
        activeBackground: '#01205f',
        size:             '2px',
      },
      step: {
        numberBackground:       '#ffffff',
        numberActiveBackground: '#01205f',
        numberColor:            '#495057',
        numberActiveColor:      '#ffffff',
        numberBorderColor:      '#c8c8c8',
        numberActiveBorderColor:'#01205f',
        numberSize:             '2rem',
        numberFontSize:         '1.143rem',
        numberBorderRadius:     '50%',
        titleColor:             '#6c757d',
        titleActiveColor:       '#495057',
        titleFontWeight:        '400',
        titleActiveFontWeight:  '600',
      },
    },
    password: {
      meter: {
        background:        '#dee2e6',
        weakBackground:    '#E53935',
        mediumBackground:  '#FFB300',
        strongBackground:  '#43A047',
        borderRadius:      '6px',
        height:            '0.5rem',
      },
    },
    fileupload: {
      progressbar: { height: '0.25rem' },
      content: {
        background:          '#ffffff',
        borderColor:         '#dee2e6',
        color:               '#495057',
        padding:             '0',
        highlightBorderColor:'#01205f',
      },
    },
    inplace: {
      display: {
        hoverBackground: '#e9ecef',
        color:           '#495057',
        hoverColor:      '#495057',
      },
    },
    inputgroup: {
      addon: {
        background:  '#e9ecef',
        borderColor: '#ced4da',
        borderWidth: '1px',
        color:       '#6c757d',
        minWidth:    '2.357rem',
        padding:     '0.5rem 0.5rem',
        borderRadius:'0',
      },
    },
  },
}) as any);

import { LocationStrategy, PathLocationStrategy } from "@angular/common";
import { routes } from "./app.routes";

import {
  InteractionType,
  IPublicClientApplication,
  PublicClientApplication
} from "@azure/msal-browser";

import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptorConfiguration,
  MsalModule,
  MsalRedirectComponent,
  MsalService
} from "@azure/msal-angular";

import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";
import { provideTinymce } from 'ngx-tinymce';
import { environment } from '../environments/environment';
import { loginRequest, msalConfig } from "./auth.config";
import { CryptoInterceptor } from './core/crypto/crypto.interceptor';
import { JwtInterceptor } from './interceptors/jwt.interceptor';
import { ApiModule } from './services/generated/api.module';

const isIE =
  window.navigator.userAgent.indexOf('MSIE ') > -1 ||
  window.navigator.userAgent.indexOf('Trident/') > -1;

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication(msalConfig);
}

// MSAL Interceptor is required to request access tokens in order to access the protected resource (Graph)
export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set(
    'https://graph.microsoft.com/v1.0/me',
    loginRequest.scopes
  );

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

// MSAL Guard is required to protect routes and require authentication before accessing protected routes
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: loginRequest,
  };
}

/**
 * A tab opened before a deploy still runs the old build, whose lazy-route chunks the new
 * deploy has removed — navigating to such a route 404s and the click silently does nothing.
 * Reload straight to the target URL so the fresh index.html/chunks load. VersionCheckService
 * normally catches deploys first; this covers the gap before its next poll.
 */
export function reloadOnStaleChunk(event: NavigationError): void {
  if (isStaleChunkError(event.error)) {
    recoverFromStaleChunk(event.url);
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    MsalRedirectComponent,
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withNavigationErrorHandler(reloadOnStaleChunk)),
    provideHttpClient(withInterceptorsFromDi()),
    providePrimeNG({ theme: { preset: FiSpokePreset, options: { cssLayer: false, darkModeSelector: 'none' } }, ripple:true }),
    importProvidersFrom([
      MsalModule,
      ApiModule.forRoot({ rootUrl: environment.apiUrl }),
    ]),
    provideTinymce({
      baseURL: '//cdnjs.cloudflare.com/ajax/libs/tinymce/5.7.1/',
    }),
    {
      provide: LocationStrategy,
      useClass: PathLocationStrategy,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: JwtInterceptor,
      multi: true,
    },
    // Must stay AFTER JwtInterceptor: request interceptors run in registration order
    // (so the Authorization header is set before the body is encrypted) and response
    // interceptors in reverse (so JwtInterceptor's error handling sees plaintext).
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CryptoInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
  ],
};
